import React, { useCallback, useEffect, useRef, useState } from "react";
import { Inbox, Send, Link, Unlink, RefreshCw, Paperclip, Reply, X, Search, Mail, KeyRound, Pencil, ChevronLeft, ChevronRight, ArrowLeft, Download, ShieldCheck, Minus } from "lucide-react";
import { mailboxApi } from "../../apis/mailbox";
import { usePermissions } from "../../hooks/usePermissions";
import "./mailbox.css";

const PROVIDERS = [{ value: "gmail", label: "Gmail / Google Workspace" }];
const APP_PASSWORD_STEPS = [
  "Open myaccount.google.com and choose Security.",
  "Turn on 2-Step Verification if it is off.",
  "Search for \"App passwords\", create one named ERP Mailbox.",
  "Copy the 16-character password Google shows and paste it below.",
];

const MAX_ATTACHMENTS = 5 * 1024 * 1024;
const PAGE_SIZE = 20;
const AVATAR_COLORS = ["#d93025", "#e37400", "#188038", "#1a73e8", "#9334e6", "#007b83", "#b31412", "#5f6368"];

const errorMessage = (error) => error?.message || "Mailbox request failed. Please try again.";
const safeName = (name) => Array.from(name, (char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127 || char === "/" || char === "\\" ? "_" : char).join("").slice(0, 180) || "attachment";
const newKey = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// "Jane Doe <jane@example.com>" -> { name: "Jane Doe", email: "jane@example.com" }. Pure text, never HTML.
function parseAddress(value) {
  const raw = String(value || "").trim();
  if (!raw) return { name: "", email: "" };
  const first = raw.split(",")[0].trim();
  const match = first.match(/^"?([^"<]*)"?\s*<([^>]+)>$/);
  if (match) return { name: match[1].trim() || match[2].trim(), email: match[2].trim() };
  return { name: first.split("@")[0] || first, email: first };
}
const initialOf = (value) => (parseAddress(value).name || "?").trim().charAt(0).toUpperCase() || "?";
function avatarColor(value) {
  let hash = 0;
  for (const char of String(value || "")) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}
// Gmail-style compact date: time for today, "Mon D" this year, otherwise the full date. Unparseable input is shown as-is.
function shortDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (date.getFullYear() === now.getFullYear()) return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return date.toLocaleDateString();
}
function longDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}
const formatSize = (size) => size >= 1024 * 1024 ? `${(size / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.ceil(size / 1024))} KB`;

async function readAttachments(files) {
  if (files.length > 10 || files.reduce((size, file) => size + file.size, 0) > MAX_ATTACHMENTS) {
    throw new Error("Choose up to 10 attachments, at most 5 MiB in total.");
  }
  return Promise.all(files.map((file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Unable to read attachment."));
    reader.onload = () => resolve({ name: safeName(file.name), type: file.type || "application/octet-stream", data: String(reader.result).split(",")[1] });
    reader.readAsDataURL(file);
  })));
}

function Avatar({ value, size = "md" }) {
  return <span className={`gm-avatar gm-avatar-${size}`} style={{ background: avatarColor(parseAddress(value).email || value) }} aria-hidden="true">{initialOf(value)}</span>;
}

// Floating Gmail-style composer (bottom-right card; full-screen on small viewports).
function Compose({ original, onClose, onSent }) {
  const [form, setForm] = useState(() => ({ key: newKey(), to: original?.reply_to || original?.from || "", cc: "", bcc: "", subject: original ? `Re: ${original.subject.replace(/^Re:\s*/i, "")}` : "", text: "", reply_to_message_id: original?.id || "", attachments: [] }));
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(null);
  const [error, setError] = useState("");
  const submitting = useRef(false);
  const heading = useRef(null);
  useEffect(() => { heading.current?.focus(); }, []);
  const update = (event) => setForm((old) => ({ ...old, [event.target.name]: event.target.value }));
  async function send() {
    if (submitting.current || attempt) return;
    submitting.current = true; setBusy(true); setError(""); setConfirm(false);
    setAttempt({ status: "pending", key: form.key });
    try {
      const result = await mailboxApi.send({ ...form, confirmed: true });
      setAttempt(result);
      if (result.status === "sent") onSent();
    } catch (err) {
      if ([400, 401, 403].includes(err?.status)) {
        // These backend responses happen before reservation/provider send. Allow explicit correction, never automatic retry.
        setAttempt(null); submitting.current = false; setError(errorMessage(err));
      } else {
        setAttempt({ status: "unknown", key: form.key });
        setError(`${errorMessage(err)} Do not resend this message. Check its status and your Sent folder first.`);
      }
    } finally { setBusy(false); }
  }
  async function check() {
    setBusy(true); setError("");
    try { const result = await mailboxApi.sendStatus(form.key); setAttempt(result); if (result.status === "sent") onSent(); }
    catch (err) { setError(`${errorMessage(err)} No automatic resend will be attempted.`); }
    finally { setBusy(false); }
  }
  const title = original ? "Reply" : "New message";
  return <section className={`mailbox-compose gm-compose ${minimized ? "gm-compose-min" : ""}`} aria-labelledby="compose-title">
    <header className="gm-compose-head">
      <h2 id="compose-title" tabIndex={-1} ref={heading}>{title}{minimized && form.subject ? ` — ${form.subject}` : ""}</h2>
      <div className="gm-compose-head-actions">
        <button type="button" className="gm-icon-btn gm-icon-btn-light" onClick={() => setMinimized((v) => !v)} aria-label={minimized ? "Expand composer" : "Minimize composer"} aria-expanded={!minimized}><Minus size={16} /></button>
        <button type="button" className="gm-icon-btn gm-icon-btn-light" onClick={onClose} disabled={busy} aria-label={attempt ? "Close without retrying" : "Close composer"}><X size={16} /></button>
      </div>
    </header>
    <div className="gm-compose-body" hidden={minimized}>
      {error && <p role="alert" className="mailbox-error">{error}</p>}
      {attempt && <div role="status" className="mailbox-notice gm-send-status"><strong>{attempt.status === "sent" ? "Message sent" : `Send ${attempt.status}`}</strong><p>{attempt.status !== "sent" && "Delivery may have occurred. Do not create a replacement or retry. Check your Sent folder before taking further action."}</p><small>Send reference: {form.key}</small>{attempt.status !== "sent" && <button type="button" disabled={busy} onClick={check}>Check send status</button>}</div>}
      <form onSubmit={(event) => { event.preventDefault(); setConfirm(true); }}>
        <fieldset disabled={busy || Boolean(attempt) || confirm}>
          <div className="gm-field-row">
            <label className="gm-field"><span>To</span><input name="to" value={form.to} onChange={update} required maxLength={8192} autoComplete="off" placeholder="Recipients" /></label>
            <div className="gm-ccbcc">
              {!showCc && <button type="button" className="gm-text-btn" onClick={() => setShowCc(true)}>Cc</button>}
              {!showBcc && <button type="button" className="gm-text-btn" onClick={() => setShowBcc(true)}>Bcc</button>}
            </div>
          </div>
          {(showCc || form.cc) && <label className="gm-field"><span>Cc</span><input name="cc" value={form.cc} onChange={update} maxLength={8192} autoComplete="off" /></label>}
          {(showBcc || form.bcc) && <label className="gm-field"><span>Bcc</span><input name="bcc" value={form.bcc} onChange={update} maxLength={8192} autoComplete="off" /></label>}
          <label className="gm-field gm-field-subject"><span className="gm-visually-hidden">Subject</span><input name="subject" value={form.subject} onChange={update} maxLength={900} readOnly={Boolean(original)} placeholder="Subject" /></label>
          <textarea className="gm-compose-text" name="text" aria-label="Message" value={form.text} onChange={update} required maxLength={1024 * 1024} rows={10} placeholder="Write your message…" />
          {form.attachments.length > 0 && <ul className="gm-chips">{form.attachments.map((file, index) => <li key={`${file.name}-${index}`} className="gm-chip"><Paperclip size={13} aria-hidden="true" /><span>{file.name}</span><button type="button" className="gm-chip-remove" aria-label={`Remove ${file.name}`} onClick={() => setForm((old) => ({ ...old, attachments: old.attachments.filter((_, i) => i !== index) }))}><X size={13} /></button></li>)}</ul>}
          <footer className="gm-compose-foot">
            <button className="mailbox-primary gm-send-btn" type="submit"><Send size={15} aria-hidden="true" /> Review and send</button>
            <label className="gm-icon-btn gm-attach" title="Attach files (10 files / 5 MiB total)"><Paperclip size={18} aria-hidden="true" /><span className="gm-visually-hidden">Attachments (10 files / 5 MiB total)</span><input type="file" multiple onChange={async (event) => {
              const files = Array.from(event.target.files || []); event.target.value = "";
              setBusy(true); setError("");
              try { const attachments = await readAttachments(files); setForm((old) => ({ ...old, attachments })); }
              catch (err) { setError(errorMessage(err)); }
              finally { setBusy(false); }
            }} /></label>
            {busy && <span role="status" className="gm-muted">Working…</span>}
          </footer>
        </fieldset>
      </form>
      {confirm && <div className="mailbox-confirm gm-confirm" role="alertdialog" aria-labelledby="send-confirm-title" aria-describedby="send-confirm-description"><h3 id="send-confirm-title">Send this email?</h3><p id="send-confirm-description">This sends a real email from your connected account to {form.to}{form.cc && `; Cc: ${form.cc}`}{form.bcc && `; Bcc: ${form.bcc}`}, with {form.attachments.length} attachment(s).</p><div className="gm-confirm-actions"><button type="button" onClick={() => setConfirm(false)} autoFocus>Keep editing</button><button type="button" className="mailbox-primary" onClick={send} disabled={busy}>Confirm send</button></div></div>}
    </div>
  </section>;
}

// ConnectEmail is the non-developer path: email + App Password, verified live by the server.
// The password is held in component state only until the request completes; it is never logged or stored here.
function ConnectEmail({ busy, setBusy, setError, onConnected, googleAvailable, onGoogle }) {
  const [form, setForm] = useState({ provider: "gmail", email: "", password: "" });
  const update = (event) => setForm((old) => ({ ...old, [event.target.name]: event.target.value }));
  async function submit(event) {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      const result = await mailboxApi.connectPassword({ provider: form.provider, email: form.email.trim(), password: form.password });
      setForm((old) => ({ ...old, password: "" }));
      onConnected(result);
    } catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  }
  return <section className="mailbox-empty mailbox-connect gm-connect-card" aria-labelledby="connect-title">
    <span className="gm-connect-icon"><KeyRound size={28} aria-hidden="true" /></span>
    <h2 id="connect-title">Connect your email</h2>
    <p>Read Inbox and Sent, and send messages after confirmation. Only you can see this mailbox in the ERP.</p>
    <form onSubmit={submit}>
      <fieldset disabled={busy}>
        <label>Email provider<select name="provider" value={form.provider} onChange={update}>{PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}</select></label>
        <label>Email address<input name="email" type="email" value={form.email} onChange={update} required maxLength={320} autoComplete="username" placeholder="name@gmail.com" /></label>
        <label>App Password<input name="password" type="password" value={form.password} onChange={update} required minLength={8} maxLength={256} autoComplete="off" placeholder="16-character App Password" /></label>
        <button className="mailbox-primary gm-connect-btn" type="submit"><Link size={16} aria-hidden="true" /> Connect email</button>
      </fieldset>
    </form>
    <details className="mailbox-help"><summary>How do I get an App Password?</summary><ol>{APP_PASSWORD_STEPS.map((step) => <li key={step}>{step}</li>)}</ol><p>An App Password is not your normal Google password. You can revoke it from the same Google page at any time.</p></details>
    {googleAvailable && <p className="mailbox-alt">Prefer not to use an App Password? <button type="button" disabled={busy} onClick={onGoogle}>Sign in with Google</button></p>}
    <p className="gm-privacy"><ShieldCheck size={14} aria-hidden="true" /> Credentials are encrypted on the server and scoped to your user.</p>
  </section>;
}

// Mount this same default component at /mailbox AND /mailbox/callback inside existing authenticated routing.
export default function MailboxPage() {
  const rights = usePermissions("MAILBOX");
  const [callback] = useState(() => {
    if (window.location.pathname !== "/mailbox/callback") return null;
    const params = new URLSearchParams(window.location.search);
    return { code: params.get("code"), state: params.get("state"), denied: params.has("error") };
  });
  const callbackStarted = useRef(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [folder, setFolder] = useState("inbox");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState("");
  const [history, setHistory] = useState([]);
  const [list, setList] = useState({ messages: [], next_page_token: "" });
  const [selected, setSelected] = useState(null);
  const [compose, setCompose] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [reload, setReload] = useState(0);
  const connected = status?.status === "connected";
  const viaGoogle = status?.method === "google";

  const loadStatus = useCallback(async () => {
    setBusy(true); setError("");
    try { setStatus(await mailboxApi.status()); }
    catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => {
    if (callback) {
      // Remove OAuth query parameters before other requests; never store/log code or state.
      window.history.replaceState(window.history.state, "", "/mailbox/callback");
      if (callbackStarted.current) return;
      callbackStarted.current = true;
      if (!rights.can_edit) { setError("Mailbox edit permission is required to finish connecting."); return; }
      if (callback.denied || !callback.code || !callback.state) { setError("Gmail authorization was cancelled or incomplete. Return to Mailbox and connect again."); return; }
      setBusy(true);
      mailboxApi.complete(callback.code, callback.state).then((result) => { setStatus(result); setNotice("Gmail connected to your user account."); }).catch((err) => setError(errorMessage(err))).finally(() => setBusy(false));
    } else if (rights.can_view) { loadStatus(); }
  }, [callback, rights.can_edit, rights.can_view, loadStatus]);
  useEffect(() => {
    if (!connected || !rights.can_view || callback) return;
    let active = true;
    setBusy(true); setError(""); setSelected(null);
    mailboxApi.list({ folder, q: query, page_token: page, count: PAGE_SIZE }).then((result) => { if (active) setList(result); }).catch((err) => { if (active) setError(errorMessage(err)); }).finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [connected, rights.can_view, folder, query, page, reload, callback]);
  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(""), 8000);
    return () => clearTimeout(timer);
  }, [notice]);

  async function connect() {
    setBusy(true); setError("");
    try {
      const result = await mailboxApi.connect();
      const destination = new URL(result.authorization_url);
      if (destination.origin !== "https://accounts.google.com" || destination.pathname !== "/o/oauth2/v2/auth" || destination.username || destination.password) throw new Error("Invalid authorization destination.");
      window.location.assign(destination.href);
    } catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  }
  async function disconnect() {
    setBusy(true); setError(""); setDisconnecting(false);
    try { setStatus(await mailboxApi.disconnect()); setSelected(null); setList({ messages: [] }); setCompose(null); }
    catch (err) { setError(errorMessage(err)); setStatus({ status: "revocation_pending" }); }
    finally { setBusy(false); }
  }
  async function open(id) {
    setBusy(true); setError("");
    try { setSelected(await mailboxApi.detail(id)); }
    catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  }
  async function download(attachment) {
    setBusy(true); setError("");
    try {
      const result = await mailboxApi.attachment(selected.id, attachment.id);
      if (result.size > MAX_ATTACHMENTS || result.data.length > Math.ceil(MAX_ATTACHMENTS / 3) * 4) throw new Error("Attachment exceeds download limit.");
      const bytes = Uint8Array.from(atob(result.data), (char) => char.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/octet-stream" }));
      const link = document.createElement("a"); link.href = url; link.download = safeName(result.name); document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  }
  const changeFolder = (value) => { setFolder(value); setPage(""); setHistory([]); setSearch(""); setQuery(""); };
  const runSearch = (event) => { event.preventDefault(); setQuery(search); setPage(""); setHistory([]); };
  const clearSearch = () => { setSearch(""); setQuery(""); setPage(""); setHistory([]); };

  if (!rights.can_view && !callback) return <main className="mailbox-page"><h1>Mailbox</h1><p role="alert">You do not have permission to view Mailbox.</p></main>;
  if (callback) return <main className="mailbox-page"><h1>Connect Gmail</h1>{busy && <p role="status">Completing secure connection…</p>}{error && <p role="alert" className="mailbox-error">{error}</p>}{notice && <p role="status">{notice}</p>}<a href="/mailbox">Return to Mailbox</a></main>;

  const folderLabel = folder === "inbox" ? "Inbox" : "Sent";
  const pageStart = history.length * PAGE_SIZE + 1;
  const pageEnd = history.length * PAGE_SIZE + list.messages.length;
  const selectedFrom = selected ? parseAddress(selected.from) : null;

  return <main className="mailbox-page gm-app" aria-busy={busy}>
    <header className="gm-topbar">
      <div className="gm-brand"><span className="gm-brand-icon"><Mail size={20} aria-hidden="true" /></span><h1>Mailbox</h1></div>
      {connected && <form role="search" className="gm-search" onSubmit={runSearch}>
        <button type="submit" className="gm-icon-btn" aria-label="Search" disabled={busy}><Search size={18} /></button>
        <input aria-label="Search mail" value={search} maxLength={512} onChange={(event) => setSearch(event.target.value)} placeholder="Search mail" />
        {(search || query) && <button type="button" className="gm-icon-btn" aria-label="Clear search" onClick={clearSearch} disabled={busy}><X size={16} /></button>}
      </form>}
      <div className="gm-topbar-actions">
        <button type="button" className="gm-icon-btn" aria-label="Refresh" title="Refresh" disabled={busy} onClick={async () => { await loadStatus(); setReload((n) => n + 1); }}><RefreshCw size={18} className={busy ? "gm-spin" : ""} /></button>
        {connected && <div className="gm-account" title={status.email}><Avatar value={status.email} size="sm" /><span className="gm-account-email">{status.email}</span></div>}
      </div>
    </header>
    {busy && <div className="gm-progress" role="status" aria-label="Loading mailbox"><span className="gm-visually-hidden">Loading mailbox…</span></div>}
    {error && <p role="alert" className="mailbox-error gm-banner">{error}</p>}
    {notice && <div role="status" className="gm-snackbar"><span>{notice}</span><button type="button" className="gm-icon-btn gm-icon-btn-light" aria-label="Dismiss notification" onClick={() => setNotice("")}><X size={14} /></button></div>}

    {!status && !busy && <div className="gm-center"><p>Mailbox status unavailable. Use Refresh to try again.</p></div>}
    {status && !connected && status.status === "revocation_pending" && <div className="gm-center"><section className="mailbox-empty gm-connect-card"><Inbox size={36} aria-hidden="true" /><h2>Mailbox disabled — revocation pending</h2><p>Use Retry disconnect to finish removing the previous Google connection before connecting again.</p>{rights.can_edit && <button type="button" disabled={busy} onClick={() => setDisconnecting(true)}><Unlink size={16} aria-hidden="true" /> Retry disconnect</button>}</section></div>}
    {status && !connected && status.status !== "revocation_pending" && <div className="gm-center">
      {status.status === "reconnect_required" && <p role="alert" className="mailbox-error">Your previous Google connection no longer works. Connect your email again below.</p>}
      {rights.can_edit
        ? <ConnectEmail busy={busy} setBusy={setBusy} setError={setError} googleAvailable={Boolean(status.google_available)} onGoogle={connect} onConnected={(result) => { setStatus((old) => ({ ...old, ...result })); setNotice("Email connected to your user account."); }} />
        : <section className="mailbox-empty gm-connect-card"><Inbox size={36} aria-hidden="true" /><h2>Connect your email</h2><p>Mailbox edit permission is required to connect.</p></section>}
    </div>}

    {disconnecting && <div className="gm-modal-backdrop"><section className="mailbox-confirm gm-dialog" role="alertdialog" aria-labelledby="disconnect-title"><h2 id="disconnect-title">Disconnect email?</h2><p>{viaGoogle ? "This disables ERP access and asks Google to revoke this connection. No email is deleted." : "This removes your saved App Password from the ERP. No email is deleted; you can also revoke the App Password in your Google Account."}</p><div className="gm-confirm-actions"><button type="button" onClick={() => setDisconnecting(false)} autoFocus>Cancel</button><button type="button" className="gm-danger" onClick={disconnect} disabled={busy}>Confirm disconnect</button></div></section></div>}

    {connected && <div className="gm-shell">
      <aside className="gm-sidebar">
        {rights.can_add && <button className="gm-compose-btn" type="button" disabled={busy || Boolean(compose)} onClick={() => setCompose({})}><Pencil size={18} aria-hidden="true" /> Compose</button>}
        <nav aria-label="Mailbox folders" className="gm-folders">
          {["inbox", "sent"].map((value) => <button key={value} type="button" className="gm-folder" aria-pressed={folder === value} disabled={busy} onClick={() => changeFolder(value)}>{value === "inbox" ? <Inbox size={18} aria-hidden="true" /> : <Send size={18} aria-hidden="true" />}<span>{value === "inbox" ? "Inbox" : "Sent"}</span></button>)}
        </nav>
        <div className="gm-sidebar-foot">
          <p className="gm-connected"><span className="gm-dot" aria-hidden="true" /> Connected via {viaGoogle ? "Google" : "App Password"}</p>
          {rights.can_edit && <button type="button" className="gm-text-btn gm-disconnect" disabled={busy} onClick={() => setDisconnecting(true)}><Unlink size={14} aria-hidden="true" /> Disconnect</button>}
        </div>
      </aside>

      <section className={`gm-main ${selected ? "gm-main-reading" : ""}`}>
        <div className="gm-list-toolbar">
          <div className="gm-list-title">
            <h2>{query ? `Results for "${query}"` : folderLabel}</h2>
            {query && <span className="gm-muted">in {folderLabel}</span>}
          </div>
          <div className="gm-pager">
            <span className="gm-muted">{list.messages.length ? `${pageStart}–${pageEnd}` : "0"}{list.next_page_token ? " of many" : ""} · Page {history.length + 1}</span>
            <button type="button" className="gm-icon-btn" aria-label="Previous" title="Newer" disabled={busy || !history.length} onClick={() => { setPage(history[history.length - 1]); setHistory((old) => old.slice(0, -1)); }}><ChevronLeft size={18} /></button>
            <button type="button" className="gm-icon-btn" aria-label="Next" title="Older" disabled={busy || !list.next_page_token} onClick={() => { setHistory((old) => [...old, page]); setPage(list.next_page_token); }}><ChevronRight size={18} /></button>
          </div>
        </div>
        <div className="gm-split">
          <section className="gm-list" aria-label={`${folder} messages`}>
            {!busy && list.messages.length === 0 && <div className="gm-empty"><Inbox size={40} aria-hidden="true" /><p>No messages found.</p><span className="gm-muted">{query ? "Try a different search." : `Nothing in ${folderLabel} yet.`}</span></div>}
            {list.messages.map((message) => {
              const who = folder === "sent" ? message.to : message.from;
              const person = parseAddress(who);
              return <button type="button" className="mailbox-row gm-row" key={message.id} aria-pressed={selected?.id === message.id} disabled={busy} onClick={() => open(message.id)}>
                <Avatar value={who} />
                <span className="gm-row-main">
                  <span className="gm-row-top"><span className="gm-row-from">{folder === "sent" ? `To: ${person.name}` : person.name}</span><time className="gm-row-date" dateTime={message.date}>{shortDate(message.date)}</time></span>
                  <span className="gm-row-bottom"><span className="gm-row-subject">{message.subject || "(No subject)"}</span>{(message.snippet || message.text) && <span className="gm-row-snippet"> — {message.snippet || message.text}</span>}</span>
                </span>
              </button>;
            })}
          </section>
          <section className="gm-reading" aria-label="Message detail">
            {selected ? <article className="gm-message">
              <div className="gm-message-bar">
                <button type="button" className="gm-icon-btn gm-back" aria-label="Back to list" onClick={() => setSelected(null)}><ArrowLeft size={18} /></button>
                <h2 className="gm-message-subject">{selected.subject || "(No subject)"}</h2>
                <span className="gm-folder-pill">{folderLabel}</span>
              </div>
              <header className="gm-message-head">
                <Avatar value={selected.from} size="lg" />
                <div className="gm-message-meta">
                  <div className="gm-message-from"><strong>{selectedFrom.name}</strong>{selectedFrom.email && selectedFrom.email !== selectedFrom.name && <span className="gm-muted"> &lt;{selectedFrom.email}&gt;</span>}</div>
                  <div className="gm-muted gm-message-to">to {selected.to || "me"}</div>
                </div>
                <div className="gm-message-side">
                  <time className="gm-muted" dateTime={selected.date}>{longDate(selected.date)}</time>
                </div>
              </header>
              {selected.html_only && <p className="mailbox-notice gm-inline-notice">HTML-only message: source shown as plain text for safety. External images and links are not loaded.</p>}
              <pre className="mailbox-body gm-body">{selected.text || "No inline text body."}</pre>
              {(selected.attachments || []).length > 0 && <footer className="gm-attachments">
                <p className="gm-attachments-title"><Paperclip size={14} aria-hidden="true" /> {selected.attachments.length} attachment{selected.attachments.length === 1 ? "" : "s"}</p>
                <ul className="mailbox-attachments gm-attachment-grid">{selected.attachments.map((file) => <li key={file.id}><button type="button" className="gm-attachment" disabled={busy || file.size > MAX_ATTACHMENTS} onClick={() => download(file)} aria-label={`Download ${file.name} (${formatSize(file.size)})`}><span className="gm-attachment-icon"><Download size={16} aria-hidden="true" /></span><span className="gm-attachment-name">{file.name}</span><span className="gm-muted">{formatSize(file.size)}</span></button>{file.size > MAX_ATTACHMENTS && <small className="gm-muted"> Exceeds 5 MiB limit</small>}</li>)}</ul>
              </footer>}
              {rights.can_add && <div className="gm-message-actions"><button type="button" className="gm-pill-btn" disabled={busy || Boolean(compose)} onClick={() => setCompose({ original: selected })}><Reply size={16} aria-hidden="true" /> Reply</button></div>}
            </article> : <div className="gm-empty gm-empty-reading"><span className="gm-empty-icon"><Mail size={34} aria-hidden="true" /></span><h2>Select a message to read</h2><p>Messages display as text only. Attachments download without a preview.</p></div>}
          </section>
        </div>
      </section>
    </div>}
    {connected && compose && rights.can_add && <Compose original={compose.original} onClose={() => setCompose(null)} onSent={() => setNotice("Message sent. Refresh Sent to view it.")} />}
  </main>;
}
